import React from 'react';
import { useState, useEffect } from 'react';
import { supabase, isSupabaseAvailable } from '../lib/supabase';
import { TenantConfig } from '../types/tenant';
import { getLocalTenant } from '../config/tenants';

interface UseSupabaseTenantResult {
  tenant: TenantConfig | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseSupabaseTenantOptions {
  enabled?: boolean;
  timeout?: number;
}

// Cache for tenant data to avoid repeated fetches
const tenantCache = new Map<string, TenantConfig>();

/**
 * Hook to fetch tenant configuration from Supabase
 */
export function useSupabaseTenant(
  subdomain: string, 
  options: UseSupabaseTenantOptions = {}
): UseSupabaseTenantResult {
  const { enabled = true, timeout = 5000 } = options;
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTenant = async () => {
    try {
      console.log('🔍 useSupabaseTenant: Fetching tenant for subdomain:', subdomain);
      setIsLoading(true);
      setError(null);
      
      if (!subdomain) {
        console.log('❌ No subdomain provided');
        setTenant(null);
        setIsLoading(false);
        return;
      }

      // Skip tenant fetching for admin subdomain
      if (subdomain === 'admin') {
        console.log('⏭️ Skipping tenant fetch for admin subdomain');
        setTenant(null);
        setIsLoading(false);
        return;
      }

      // Check cache first
      if (tenantCache.has(subdomain)) {
        console.log('💾 Found cached tenant for:', subdomain);
        const cachedTenant = tenantCache.get(subdomain)!;
        setTenant(cachedTenant);
        setIsLoading(false);
        return;
      }

      // Try Supabase first if available
      if (isSupabaseAvailable()) {
        console.log('🔗 Querying Supabase for tenant:', subdomain);
        
        // Create timeout promise
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Query timeout')), timeout);
        });
        
        // Create Supabase query promise
        const queryPromise = supabase!
          .from('cemeteries')
          .select('*')
          .eq('subdomain', subdomain)
          .eq('is_active', true)
          .single();

        // Race between query and timeout
        try {
          const { data, error: fetchError } = await Promise.race([
            queryPromise,
            timeoutPromise
          ]) as any;

          console.log('🔍 Supabase query result:', { data, error: fetchError });
          console.log('📊 Theme config from DB:', data?.theme_config);

          if (!fetchError && data) {
            console.log('✅ Found tenant in Supabase:', data.name);
            console.log('🎨 Theme config data:', data.theme_config);
            // Transform Supabase data to TenantConfig
            const tenantConfig: TenantConfig = {
              id: data.id,
              subdomain: data.subdomain,
              name: data.name,
              displayName: data.name,
              description: data.description || `Professional cemetery services by ${data.name}`,
              themeConfig: data.theme_config || {},
              branding: {
                primaryColor: data.branding?.primaryColor || '#EA6D2C',
                secondaryColor: data.branding?.secondaryColor || '#F5F1E8',
                backgroundColor: data.branding?.backgroundColor || '#FAFAFA',
                textColor: data.branding?.textColor || '#1F2937',
                logoUrl: data.branding?.logoUrl,
                faviconUrl: data.branding?.faviconUrl,
                fonts: data.branding?.fonts || {
                  heading: 'Merriweather',
                  body: 'Open Sans'
                }
              },
              contact: {
                phone: data.business_rules?.contactInfo?.phone || '(03) 8359 0111',
                email: data.business_rules?.contactInfo?.email || 'info@cemetery.com.au',
                address: data.business_rules?.contactInfo?.address || '123 Cemetery Road, Melbourne VIC 3000',
                emergencyPhone: data.business_rules?.contactInfo?.emergencyPhone,
                website: data.business_rules?.contactInfo?.website,
              },
              features: {
                chatEnabled: data.features?.chat ?? true,
                servicesEnabled: data.features?.services ?? true,
                searchEnabled: data.features?.search ?? true,
                workOrderEnabled: data.features?.workRequests ?? true,
                salesEnabled: data.features?.sales ?? true,
                videosEnabled: data.features?.videos ?? true,
                feedbackEnabled: data.features?.feedback ?? true,
                contactEnabled: data.features?.contact ?? true,
              },
              businessHours: data.business_rules?.operatingHours || {
                monday: { open: '09:00', close: '17:00' },
                tuesday: { open: '09:00', close: '17:00' },
                wednesday: { open: '09:00', close: '17:00' },
                thursday: { open: '09:00', close: '17:00' },
                friday: { open: '09:00', close: '17:00' },
                saturday: { open: '09:00', close: '16:00' },
                sunday: { open: '09:00', close: '16:00' },
              },
              multiLocationMode: data.features?.multiLocation ?? false,
              timezone: data.business_rules?.timezone || 'Australia/Melbourne',
              isActive: data.is_active,
              createdAt: data.created_at,
              updatedAt: data.updated_at,
              // UI Configuration
              uiConfig: {
                theme: data.ui_config?.theme || 'light',
                fontSize: {
                  base: data.ui_config?.fontSize?.base || '16px',
                  large: data.ui_config?.fontSize?.large || '18px',
                  small: data.ui_config?.fontSize?.small || '14px',
                },
                spacing: {
                  base: data.ui_config?.spacing?.base || '16px',
                  gap: data.ui_config?.spacing?.gap || '24px',
                },
                layout: {
                  maxWidth: data.ui_config?.layout?.maxWidth || '1200px',
                  headerHeight: data.ui_config?.layout?.headerHeight || '60px',
                },
                components: {
                  buttonSize: data.ui_config?.components?.buttonSize || 'medium',
                  cornerRadius: data.ui_config?.components?.cornerRadius || '8px',
                }
              },
              // AI Configuration
              aiConfig: {
                enabled: data.ai_config?.enabled ?? true,
                provider: data.ai_config?.provider || 'openai',
                model: data.ai_config?.model || 'gpt-3.5-turbo',
                temperature: data.ai_config?.temperature || 0.7,
                maxTokens: data.ai_config?.maxTokens || 1000,
                systemPrompt: data.ai_config?.systemPrompt,
                knowledgeBase: data.ai_config?.knowledgeBase ?? true,
                autoRespond: data.ai_config?.autoRespond ?? false,
              }
            };

            // Cache the result
            tenantCache.set(subdomain, tenantConfig);
            setTenant(tenantConfig);
            setIsLoading(false);
            return;
          } else if (fetchError) {
            console.log('⚠️ Supabase query error:', fetchError.message);
            // Don't throw here, fall through to local config
          }
        } catch (queryError) {
          console.log('⚠️ Supabase query failed:', queryError);
          // Fall through to local config
        }
      }

      // Fall back to local config
      console.log('🏠 Trying local tenant config for:', subdomain);
      const localTenant = getLocalTenant(subdomain);
      if (localTenant) {
        console.log('✅ Found local tenant config:', localTenant.name);
        // Convert local config to full TenantConfig
        const tenantConfig: TenantConfig = {
          ...localTenant,
          uiConfig: {
            theme: 'light',
            fontSize: {
              base: '16px',
              large: '18px',
              small: '14px',
            },
            spacing: {
              base: '16px',
              gap: '24px',
            },
            layout: {
              maxWidth: '1200px',
              headerHeight: '60px',
            },
            components: {
              buttonSize: 'medium',
              cornerRadius: '8px',
            }
          },
          aiConfig: {
            enabled: true,
            provider: 'openai',
            model: 'gpt-3.5-turbo',
            temperature: 0.7,
            maxTokens: 1000,
            knowledgeBase: true,
            autoRespond: false,
          }
        };

        tenantCache.set(subdomain, tenantConfig);
        setTenant(tenantConfig);
      } else {
        console.log('❌ No tenant found for subdomain:', subdomain);
        throw new Error(`No configuration found for subdomain: ${subdomain}`);
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load tenant configuration';
      console.error('❌ useSupabaseTenant error:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const refetch = async () => {
    console.log('🔄 Refetching tenant for:', subdomain);
    // Clear cache for this subdomain
    tenantCache.delete(subdomain);
    await fetchTenant();
  };

  useEffect(() => {
    if (subdomain && enabled) {
      console.log('🚀 Starting tenant fetch for:', subdomain);
      fetchTenant();
    } else {
      console.log('⏸️ Tenant fetch skipped - subdomain:', subdomain, 'enabled:', enabled);
      setIsLoading(false);
    }
  }, [subdomain, enabled, timeout]);

  return {
    tenant,
    isLoading,
    error,
    refetch
  };
}